/**
 * Chunked file upload for large GL / TB / Aging files.
 *
 * Fixes the Cloudflare 524 timeout that hit on 450k-row uploads:
 * a 60 MB XLSX at typical uplink speeds takes 60-90 s to POST as a
 * single body — closer to Cloudflare's 120 s Proxy Read Timeout than
 * we want to be. Splitting into 4 MB chunks keeps every request under
 * ~15 s so the timeout is never in play.
 *
 * Small files (< 5 MB) skip this and POST /gl/preview directly.
 */
import api from "@/lib/api";

// Files at or above this size use the chunked flow. Matches the
// backend's /gl/preview sync threshold — files this big are also the
// ones the backend would route to the async job runner anyway.
export const CHUNKED_UPLOAD_THRESHOLD = 5 * 1024 * 1024;

/**
 * Upload `file` in 4 MB chunks and kick off the async preview job.
 *
 * @param {File} file - The file the user picked.
 * @param {(pct:number, msg:string) => void} [onProgress] - Called on
 *   every chunk finish with cumulative upload percent (0-100).
 * @returns {Promise<{job_id:string, async:true, filename:string}>}
 *   Same shape as the direct /gl/preview async path — poll /jobs/{id}.
 */
export async function uploadGLChunked(file, onProgress) {
  // 1. init — server returns a fresh upload_id + the chunk size to use.
  // Round 3 Batch D fix: declare total_chunks up-front so the server-side
  // completeness check (server.py:1958-1969, H8 audit fix) actually
  // fires for the real client. Without this the frontend never sent
  // total_chunks, session.total_chunks stayed None, and the "expected
  // vs received" comparison silently short-circuited — a truncated-
  // but-contiguous upload (e.g. last chunk dropped by proxy) would
  // still finalize.
  // We compute against the same default chunk size the server
  // returns; if the server overrides via init.chunk_size we trust
  // that too (and fall back to a stricter re-check post-init below).
  const ASSUMED_CHUNK = 4 * 1024 * 1024;
  const declaredTotal = Math.max(1, Math.ceil(file.size / ASSUMED_CHUNK));
  const init = await api.post("/gl/upload-init",
    { total_chunks: declaredTotal, filename: file.name },
    { _silent: true });
  const uploadId = init.data.upload_id;
  const chunkSize = init.data.chunk_size || ASSUMED_CHUNK;
  const total = Math.ceil(file.size / chunkSize);
  // If the server's chunk_size disagrees with our assumption the
  // declared total is stale — the finalize-time coverage-check on
  // chunk_index still protects us (server.py:1970-1981), but log so
  // this doesn't fail silently in dev.
  if (total !== declaredTotal && typeof console !== "undefined") {
    console.warn(
      `[chunkedUpload] server chunk_size (${chunkSize}) differs from ` +
      `client assumption (${ASSUMED_CHUNK}); declared ${declaredTotal} ` +
      `chunks, will send ${total}.`);
  }

  // 2. upload chunks sequentially — parallelism gains little given the
  // proxy already bounds each request, and adds server memory pressure
  // on very large files.
  for (let i = 0; i < total; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const blob = file.slice(start, end);
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("chunk_index", String(i));
    fd.append("chunk", blob, `chunk-${i}`);
    await api.post("/gl/upload-chunk", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000, _silent: true,
    });
    if (onProgress) {
      const pct = Math.round(((i + 1) / total) * 100);
      onProgress(pct, `Uploading… ${i + 1} / ${total} chunks`);
    }
  }
  // 3. finalize — server queues the preview job and returns { job_id }
  const fin = await api.post("/gl/upload-finalize", {
    upload_id: uploadId, filename: file.name,
  }, { _silent: true });
  return fin.data;
}
